import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      // If signed out (possibly from another tab), wipe any draft + cart state
      // and force a clean home navigation so protected pages aren't shown.
      if (event === 'SIGNED_OUT') {
        try {
          sessionStorage.removeItem('appState');
          // Keep ovrRegDraft so users who get prompted to log in mid-roster
          // can resume after signing back in. The recovery banner gates on
          // env (prod vs local) and step, so it's safe to leave behind.
          localStorage.removeItem('ovr_pending_payment');
          localStorage.removeItem('ovr_cart_v1');
        } catch (e) { /* ignore storage failures */ }
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Preserve current URL so users return to their in-progress flow after OAuth
  const signInWithGoogle = () =>
    supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.href },
    });

  const signInWithEmail = (email, password) =>
    supabase.auth.signInWithPassword({ email, password });

  const signUpWithEmail = (email, password, displayName) =>
    supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: displayName },
        emailRedirectTo: window.location.href,
      },
    });

  // Global scope kills the refresh token on the server so other tabs/devices sign out too
  const signOut = () => supabase.auth.signOut({ scope: 'global' });

  const resetPassword = (email) =>
    supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/?password_reset=1`,
    });

  return (
    <AuthContext.Provider value={{ user, loading, signInWithGoogle, signInWithEmail, signUpWithEmail, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
